import { useState } from "react";
import { ChevronRight, CheckCircle, Clock, BarChart2, BookMarked, Send, Layers } from "lucide-react";
import { ACCENT, ACCENT_LIGHT, ACCENT_MID } from "../constants/theme";
import { initialPrograms } from "../data/programs";
import Btn from "../components/Button";
import Card from "../components/Card";
import SectionLabel from "../components/SectionLabel";
import Input from "../components/Input";
import Select from "../components/Select";

export default function TrainingPrograms() {
  const [programs, setPrograms] = useState(initialPrograms);
  const [enrollForm, setEnrollForm] = useState({ name: "", email: "", phone: "", program: "", message: "" });
  const [enrollErrors, setEnrollErrors] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [enrollSuccess, setEnrollSuccess] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProgram, setNewProgram] = useState({ title: "", duration: "", skillLevel: "", courseType: "", description: "" });
  const [editId, setEditId] = useState(null);

  const skillColors = { Beginner: "#16a34a", Intermediate: "#d97706", Advanced: "#7c3aed" };
  const skillBg = { Beginner: "#f0fdf4", Intermediate: "#fffbeb", Advanced: "#f5f3ff" };

  const validateEnroll = () => {
    const e = {};
    if (!enrollForm.name.trim()) e.name = "Name is required";
    if (!enrollForm.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) e.email = "Valid email required";
    if (!enrollForm.phone.trim()) e.phone = "Phone is required";
    if (!enrollForm.program) e.program = "Please select a program";
    return e;
  };

  const submitEnroll = () => {
    const e = validateEnroll();
    if (Object.keys(e).length) { setEnrollErrors(e); return; }
    setSubmissions(p => [...p, { ...enrollForm, id: Date.now(), submittedAt: new Date().toLocaleString() }]);
    setEnrollSuccess(true);
    setEnrollForm({ name: "", email: "", phone: "", program: "", message: "" });
    setEnrollErrors({});
  };

  const saveProgram = () => {
    if (!newProgram.title.trim() || !newProgram.duration.trim()) return;
    if (editId) {
      setPrograms(p => p.map(pr => pr.id === editId ? { ...newProgram, id: editId } : pr));
      setEditId(null);
    } else {
      setPrograms(p => [...p, { ...newProgram, id: Date.now() }]);
    }
    setNewProgram({ title: "", duration: "", skillLevel: "", courseType: "", description: "" });
    setShowAddForm(false);
  };

  const startEdit = (pr) => {
    setNewProgram({ title: pr.title, duration: pr.duration, skillLevel: pr.skillLevel, courseType: pr.courseType, description: pr.description });
    setEditId(pr.id);
    setShowAddForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProgram = (id) => setPrograms(p => p.filter(pr => pr.id !== id));

  return (
    <div>
      <section style={{ background: ACCENT_LIGHT, padding: "72px 24px 56px", textAlign: "center" }}>
        <SectionLabel>Learn & Grow</SectionLabel>
        <h1 style={{ fontSize: "clamp(30px, 5vw, 52px)", fontWeight: 900, color: "#0f172a", letterSpacing: "-1px", marginBottom: 14 }}>Training Programs</h1>
        <p style={{ color: "#475569", fontSize: 16, maxWidth: 520, margin: "0 auto 28px" }}>Certified, practical, and designed for every kind of creator.</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <Btn onClick={() => setShowAddForm(!showAddForm)}>
            {showAddForm ? "Close Form" : editId ? "Edit Program" : "Add New Program"} <Layers size={16} />
          </Btn>
          <Btn outline onClick={() => setShowAdmin(!showAdmin)}>
            {showAdmin ? "Hide" : "View"} Responses ({submissions.length}) <BarChart2 size={16} />
          </Btn>
        </div>
      </section>

      {/* Admin: Add/Edit Program */}
      {showAddForm && (
        <section style={{ background: "#f8fafc", padding: "48px 24px", borderBottom: "1px solid #e2e8f0" }}>
          <div style={{ maxWidth: 700, margin: "0 auto" }}>
            <Card style={{ padding: 36 }}>
              <h2 style={{ fontWeight: 800, fontSize: 20, color: "#0f172a", marginBottom: 24 }}>{editId ? "Edit Program" : "Add New Program"}</h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                {[
                  { key: "title", label: "Program Title", placeholder: "e.g. Voice Over Mastery" },
                  { key: "duration", label: "Duration", placeholder: "e.g. 6 Weeks" },
                ].map(({ key, label, placeholder }) => (
                  <Input key={key} label={label} value={newProgram[key]} placeholder={placeholder} required
                    onChange={e => setNewProgram(p => ({ ...p, [key]: e.target.value }))} />
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" }}>
                <Select label="Skill Level" value={newProgram.skillLevel} options={["Beginner", "Intermediate", "Advanced"]}
                  onChange={e => setNewProgram(p => ({ ...p, skillLevel: e.target.value }))} />
                <Select label="Course Type" value={newProgram.courseType} options={["Online", "Hybrid", "In-Person"]}
                  onChange={e => setNewProgram(p => ({ ...p, courseType: e.target.value }))} />
              </div>
              <Input label="Description" as="textarea" rows={3} value={newProgram.description} placeholder="Program overview..."
                onChange={e => setNewProgram(p => ({ ...p, description: e.target.value }))} />
              <div style={{ display: "flex", gap: 12 }}>
                <Btn onClick={saveProgram}>{editId ? "Update Program" : "Add Program"} <CheckCircle size={16} /></Btn>
                <Btn outline onClick={() => { setShowAddForm(false); setEditId(null); setNewProgram({ title: "", duration: "", skillLevel: "", courseType: "", description: "" }); }}>Cancel</Btn>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Programs Grid */}
      <section style={{ background: "#fff", padding: "72px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 24 }}>
            {programs.map(pr => (
              <Card key={pr.id} style={{ display: "flex", flexDirection: "column" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                  <div style={{ width: 44, height: 44, background: ACCENT_LIGHT, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <BookMarked size={20} color={ACCENT} />
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => startEdit(pr)} style={{ background: "#f1f5f9", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, color: "#475569", fontWeight: 600 }}>Edit</button>
                    <button onClick={() => deleteProgram(pr.id)} style={{ background: "#fff1f2", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, color: "#ef4444", fontWeight: 600 }}>Delete</button>
                  </div>
                </div>
                <h3 style={{ fontWeight: 700, fontSize: 17, color: "#0f172a", marginBottom: 10 }}>{pr.title}</h3>
                <p style={{ color: "#64748b", fontSize: 13.5, lineHeight: 1.65, flex: 1, marginBottom: 18 }}>{pr.description}</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, background: ACCENT_LIGHT, color: ACCENT, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
                    <Clock size={12} />{pr.duration}
                  </span>
                  <span style={{ background: skillBg[pr.skillLevel] || "#f1f5f9", color: skillColors[pr.skillLevel] || "#374151", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>{pr.skillLevel}</span>
                  <span style={{ background: "#f8fafc", color: "#475569", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>{pr.courseType}</span>
                </div>
                <Btn onClick={() => { document.getElementById("enroll-form")?.scrollIntoView({ behavior: "smooth" }); setEnrollForm(f => ({ ...f, program: pr.title })); }}
                  style={{ width: "100%", justifyContent: "center" }}>Enroll Now <ChevronRight size={16} /></Btn>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enroll Form */}
      <section id="enroll-form" style={{ background: "#f8fafc", padding: "72px 24px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <SectionLabel>Enroll Now</SectionLabel>
            <h2 style={{ fontSize: 32, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.6px" }}>Reserve Your Spot</h2>
          </div>
          {enrollSuccess && (
            <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: "16px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 10 }}>
              <CheckCircle size={20} color="#16a34a" />
              <p style={{ color: "#15803d", fontWeight: 600, fontSize: 14 }}>Enrollment submitted! We'll be in touch shortly.</p>
            </div>
          )}
          <Card style={{ padding: 36 }}>
            {[
              { key: "name", label: "Full Name", type: "text", placeholder: "Your full name" },
              { key: "email", label: "Email Address", type: "email", placeholder: "you@example.com" },
              { key: "phone", label: "Phone Number", type: "tel", placeholder: "+91 98765 43210" },
            ].map(({ key, label, type, placeholder }) => (
              <div key={key}>
                <Input label={label} type={type} value={enrollForm[key]} placeholder={placeholder} required
                  onChange={e => { setEnrollForm(p => ({ ...p, [key]: e.target.value })); setEnrollErrors(p => ({ ...p, [key]: "" })); }} />
                {enrollErrors[key] && <p style={{ color: "#ef4444", fontSize: 12.5, marginTop: -14, marginBottom: 10 }}>{enrollErrors[key]}</p>}
              </div>
            ))}
            <div>
              <Select label="Select Training Program" value={enrollForm.program} options={programs.map(p => p.title)} required
                onChange={e => { setEnrollForm(p => ({ ...p, program: e.target.value })); setEnrollErrors(p => ({ ...p, program: "" })); }} />
              {enrollErrors.program && <p style={{ color: "#ef4444", fontSize: 12.5, marginTop: -14, marginBottom: 10 }}>{enrollErrors.program}</p>}
            </div>
            <Input label="Message / Reason for Joining" as="textarea" rows={4} value={enrollForm.message}
              placeholder="Tell us what you hope to gain from this program..." onChange={e => setEnrollForm(p => ({ ...p, message: e.target.value }))} />
            <Btn onClick={submitEnroll} style={{ width: "100%", justifyContent: "center" }}>Submit Enrollment <Send size={16} /></Btn>
          </Card>
        </div>
      </section>

      {/* Admin Responses */}
      {showAdmin && (
        <section style={{ background: "#fff", padding: "64px 24px" }}>
          <div style={{ maxWidth: 1100, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ fontWeight: 800, fontSize: 24, color: "#0f172a" }}>Admin: Enrollment Responses</h2>
              <span style={{ background: ACCENT_LIGHT, color: ACCENT, borderRadius: 20, padding: "6px 16px", fontWeight: 700, fontSize: 13 }}>{submissions.length} total</span>
            </div>
            {submissions.length === 0 ? (
              <div style={{ textAlign: "center", padding: "48px 24px", color: "#94a3b8", fontSize: 15 }}>No submissions yet.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      {["Name", "Email", "Phone", "Program", "Message", "Submitted At"].map(h => (
                        <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((s, i) => (
                      <tr key={s.id} style={{ background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                        {[s.name, s.email, s.phone, s.program, s.message || "—", s.submittedAt].map((val, j) => (
                          <td key={j} style={{ padding: "12px 16px", color: "#475569", borderBottom: "1px solid #f1f5f9", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: j < 4 ? "nowrap" : "normal" }}>{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
